import React, { useState, useEffect } from 'react';
import { Heart, DollarSign, MessageSquare, Trash2, Settings, Loader2, ShieldCheck } from 'lucide-react';
import TeamThreadDialog from './TeamThreadDialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { formatDistanceToNow } from 'date-fns';
import { apiService } from '../services/api';
import { useToast } from '../hooks/use-toast';
import TipResponseModal from './TipResponseModal';
import NotificationSettings from './NotificationSettings';

const NotificationList = ({ onNotificationRead, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTipModal, setShowTipModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedTip, setSelectedTip] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiService.notifications.getNotifications({ limit: 20 });
      if (response.data.success) {
        setNotifications(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast({
        title: "Error",
        description: "Failed to load notifications.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId, isRead) => {
    if (isRead) return; // Already read
    
    try {
      await apiService.notifications.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true }
            : notif
        )
      );
      
      onNotificationRead?.();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await apiService.notifications.deleteNotification(notificationId);
      
      // Remove from local state
      setNotifications(prev => 
        prev.filter(notif => notif.id !== notificationId)
      );
      
      toast({
        title: "Deleted",
        description: "Notification deleted successfully."
      });
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast({
        title: "Error",
        description: "Failed to delete notification.",
        variant: "destructive"
      });
    }
  };

  const handleTipResponse = (notification) => {
    // For tip notifications, show tip response modal
    if (notification.notification_type === 'tip_received' && notification.related_data) {
      setSelectedTip({
        swipe_id: notification.related_data.swipe_id,
        from_user_name: notification.related_user_name,
        amount: notification.related_data.tip_amount,
        message: notification.related_data.tip_message
      });
      setShowTipModal(true);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'swipe_accepted':
      case 'match_created':
        return <Heart className="w-4 h-4 text-red-500" />;
      case 'tip_accepted':
      case 'tip_rejected':
      case 'tip_countered':
      case 'tip_received':
        return <DollarSign className="w-4 h-4 text-green-500" />;
      case 'admin_message':
        return <ShieldCheck className="w-4 h-4 text-rose-600" />;
      default:
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
    }
  };

  const getNotificationColor = (type, isRead) => {
    if (isRead) return 'bg-gray-50';
    
    switch (type) {
      case 'swipe_accepted':
      case 'match_created':
        return 'bg-red-50 border-l-red-500';
      case 'tip_accepted':
        return 'bg-green-50 border-l-green-500';
      case 'tip_rejected':
        return 'bg-orange-50 border-l-orange-500';
      case 'tip_countered':
      case 'tip_received':
        return 'bg-blue-50 border-l-blue-500';
      case 'admin_message':
        return 'bg-rose-50 border-l-rose-500';
      default:
        return 'bg-blue-50 border-l-blue-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <>
      <div className="max-h-96">
        <ScrollArea className="h-full">
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No notifications yet</p>
              <p className="text-xs text-gray-400 mt-1">
                You'll see matches, tips, and other updates here
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {notifications.map((notification) => (
                <Card 
                  key={notification.id}
                  className={`border-0 border-l-4 rounded-l-none cursor-pointer hover:bg-gray-50 transition-colors ${
                    getNotificationColor(notification.notification_type, notification.is_read)
                  }`}
                  onClick={() => { handleMarkAsRead(notification.id, notification.is_read); if (notification.notification_type === 'admin_message' && notification.related_data?.admin_message_id) setThreadId(notification.related_data.admin_message_id); }}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.notification_type)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900">
                              {notification.title}
                            </p>
                            {!notification.is_read && (
                              <Badge 
                                variant="destructive" 
                                className="w-2 h-2 rounded-full p-0"
                              />
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-600 mt-1">
                            {notification.message}
                          </p>
                          
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-gray-400">
                              {formatDistanceToNow(new Date(notification.created_at), { 
                                addSuffix: true 
                              })}
                            </p>
                            
                            {notification.notification_type === 'tip_received' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs py-1 px-2 h-6"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTipResponse(notification);
                                }}
                              >
                                Respond
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-1 h-6 w-6 text-gray-400 hover:text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNotification(notification.id);
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {notifications.length > 0 && (
          <div className="border-t border-gray-200 p-3 bg-gray-50">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="text-gray-600 hover:text-gray-700 text-xs"
              >
                <Settings className="w-3 h-3 mr-1" />
                Settings
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    await apiService.notifications.deleteAllNotifications();
                    setNotifications([]);
                    toast({
                      title: "All Clear! 🗑️",
                      description: "All notifications deleted."
                    });
                  } catch (error) {
                    toast({
                      title: "Error",
                      description: "Failed to delete notifications.",
                      variant: "destructive"
                    });
                  }
                }}
                className="text-red-600 hover:text-red-700 text-xs"
              >
                Clear All
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Tip Response Modal */}
      {showTipModal && selectedTip && (
        <TipResponseModal
          isOpen={showTipModal}
          onClose={() => {
            setShowTipModal(false);
            setSelectedTip(null);
          }}
          tipData={selectedTip}
          onResponse={() => {
            fetchNotifications(); // Refresh notifications after response
            onClose?.(); // Close the notification popover
          }}
        />
      )}

      {/* Notification Settings */}
      {showSettings && (
        <NotificationSettings
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
      )}
      <TeamThreadDialog threadId={threadId} open={!!threadId} onOpenChange={(o) => !o && setThreadId(null)} />
    </>
  );
};

export default NotificationList;